import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Camera,
  User,
  Home,
  Globe,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Save,
  X,
  Upload,
  Phone,
  Mail,
  ShieldCheck,
  Hash,
  FileText,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../../api/axiosInstance";
import {
  fetchCountries,
  fetchStates,
  fetchDistricts,
  fetchLocalAreas,
} from "../../../../features/master/masterThunks";
import { clearStates, clearDistricts, clearLocalAreas } from "../../../../features/master/masterSlice";

// ---------------- REUSABLE INPUT ----------------
const FormInput = ({ icon: Icon, isTextArea = false, className = "", disabled = false, ...props }) => (
  <div className="form-group">
    <div className={`input-container ${isTextArea ? "large" : ""} ${className}`}>
      {Icon && <Icon className="input-icon" size={18} />}
      {isTextArea ? (
        <textarea className="form-input large" disabled={disabled} {...props} />
      ) : (
        <input className={`form-input ${className}`} disabled={disabled} {...props} />
      )}
    </div>
  </div>
);

// ---------------- REUSABLE SELECT ----------------
const FormSelect = ({ icon: Icon, options = [], loading = false, ...props }) => (
  <div className="form-group">
    <div className="input-container">
      {Icon && <Icon className="input-icon" size={18} />}
      <select className="form-input" disabled={loading || props.disabled} {...props}>
        <option value="">
          {loading ? "Loading..." : props.placeholder || "Select"}
        </option>
        {options.map((opt) => (
          <option key={opt._id || opt.id} value={opt._id || opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const KYC_ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

// ---------------- KYC FILE INPUT ----------------
const KycFileInput = ({ label, preview, inputRef, onChange }) => (
  <div className="kyc-file-input">
    <input
      type="file"
      ref={inputRef}
      onChange={onChange}
      accept="image/jpeg,image/jpg,image/png,application/pdf,.doc,.docx"
      style={{ display: 'none' }}
    />
    <div className="kyc-file-label">
      <FileText size={15} />
      <span>{label}</span>
    </div>
    {preview && preview !== '__pdf__' && (
      <img src={preview} alt={label} className="kyc-file-preview" />
    )}
    {preview === '__pdf__' && (
      <span className="kyc-file-pdf-badge">PDF / DOC uploaded</span>
    )}
    <button
      type="button"
      className="kyc-file-btn"
      onClick={() => inputRef.current?.click()}
    >
      <Upload size={14} />
      {preview ? 'Replace' : 'Upload'}
    </button>
  </div>
);

const EditProfile = ({ onClose, onSave }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const aadhaarFrontRef = useRef(null);
  const aadhaarBackRef = useRef(null);
  const panFrontRef = useRef(null);
  const panBackRef = useRef(null);

  // Redux state
  const { countries, states, districts, localAreas, loading: masterLoading } = useSelector(
    (state) => state.master
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [initialData, setInitialData] = useState(null);

  const [formData, setFormData] = useState({
    photo: null,
    photoPreview: null,
    fullName: "",
    mobileNumber: "",
    email: "",
    sonDaughterOf: "",
    dob: "",
    streetAddress: "",
    countryId: "",
    stateId: "",
    districtId: "",
    localAreaId: "",
    pincode: "",
    aadhaarNumber: "",
    panNumber: "",
    aadhaarFront: null,
    aadhaarFrontPreview: null,
    aadhaarBack: null,
    aadhaarBackPreview: null,
    panFront: null,
    panFrontPreview: null,
    panBack: null,
    panBackPreview: null,
    facebookProfile: "",
    twitterProfile: "",
    instagramProfile: "",
  });

  // Fetch current profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/api/auth/me");
        const profileData = res.data.data;
        setProfile(profileData);

        const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || '';
        const kycPreview = (path) => path ? `${baseUrl}${path}` : null;

        const initialFormData = {
          photo: null,
          photoPreview: profileData.photoUrl
            ? `${baseUrl}${profileData.photoUrl}`
            : null,
          fullName: profileData.fullName || "",
          mobileNumber: profileData.mobileNumber || "",
          email: profileData.email || "",
          sonDaughterOf: profileData.sonDaughterOf || "",
          dob: profileData.dob ? profileData.dob.split('T')[0] : "",
          streetAddress: profileData.address?.streetAddress || "",
          countryId: profileData.address?.country?.id || "",
          stateId: profileData.address?.state?.id || "",
          districtId: profileData.address?.district?.id || "",
          localAreaId: profileData.address?.localArea?.id || "",
          pincode: profileData.kyc?.pincode || "",
          aadhaarNumber: profileData.kyc?.aadhaarNumber || "",
          panNumber: profileData.kyc?.panNumber || "",
          aadhaarFront: null,
          aadhaarFrontPreview: kycPreview(profileData.kyc?.aadhaarFront),
          aadhaarBack: null,
          aadhaarBackPreview: kycPreview(profileData.kyc?.aadhaarBack),
          panFront: null,
          panFrontPreview: kycPreview(profileData.kyc?.panFront),
          panBack: null,
          panBackPreview: kycPreview(profileData.kyc?.panBack),
          facebookProfile: profileData.socialLinks?.facebookProfile || "",
          twitterProfile: profileData.socialLinks?.twitterProfile || "",
          instagramProfile: profileData.socialLinks?.instagramProfile || "",
        };

        setFormData(initialFormData);
        setInitialData(initialFormData); // Store initial data for comparison

        setLoading(false);
      } catch (err) {
        console.error("Profile fetch failed:", err);
        toast.error("Failed to load profile");
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Fetch countries on mount
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // Fetch states when country changes or on initial load
  useEffect(() => {
    if (formData.countryId) {
      dispatch(fetchStates(formData.countryId));
    }
  }, [formData.countryId, dispatch]);

  // Fetch districts when state changes or on initial load
  useEffect(() => {
    if (formData.stateId) {
      dispatch(fetchDistricts(formData.stateId));
    }
  }, [formData.stateId, dispatch]);

  // Fetch local areas when district changes or on initial load
  useEffect(() => {
    if (formData.districtId) {
      dispatch(fetchLocalAreas(formData.districtId));
    }
  }, [formData.districtId, dispatch]);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }

      updateFormData({
        photo: file,
        photoPreview: URL.createObjectURL(file)
      });
    }
  };

  const handleKycFile = (field) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!KYC_ALLOWED_TYPES.includes(file.type)) {
      toast.error("Only images (JPG, PNG), PDF, or DOC files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be less than 5MB");
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.type.includes('word');
    updateFormData({
      [field]: file,
      [`${field}Preview`]: isPdf ? '__pdf__' : URL.createObjectURL(file),
    });
  };

  // Handle country change
  const handleCountryChange = (e) => {
    const newCountryId = e.target.value;
    updateFormData({
      countryId: newCountryId,
      stateId: "",
      districtId: "",
      localAreaId: ""
    });
  };

  // Handle state change
  const handleStateChange = (e) => {
    const newStateId = e.target.value;
    updateFormData({
      stateId: newStateId,
      districtId: "",
      localAreaId: ""
    });
  };

  // Handle district change
  const handleDistrictChange = (e) => {
    const newDistrictId = e.target.value;
    updateFormData({
      districtId: newDistrictId,
      localAreaId: ""
    });
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      toast.error("Please enter full name");
      return false;
    }
    // Email is OPTIONAL
if (formData.email?.trim()) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email)) {
    toast.error("Please enter a valid email");
    return false;
  }
}

    if (!formData.sonDaughterOf.trim()) {
      toast.error("Please enter parent's name");
      return false;
    }
    if (!formData.dob) {
      toast.error("Please select date of birth");
      return false;
    }
    const maxDob = new Date();
    maxDob.setFullYear(maxDob.getFullYear() - 18);
    if (new Date(formData.dob) > maxDob) {
      toast.error("You must be at least 18 years old");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    
    try {
      const formDataToSend = new FormData();

      // Only add changed fields to FormData
      // Photo - only if changed
      if (formData.photo) {
        formDataToSend.append("photoUrl", formData.photo);
      }

      // Basic Info - always send if changed
      if (formData.fullName !== initialData.fullName) {
        formDataToSend.append("fullName", formData.fullName.trim());
      }
      
      if (formData.mobileNumber !== initialData.mobileNumber) {
        formDataToSend.append("mobileNumber", formData.mobileNumber.trim());
      }
      
      if (formData.email !== initialData.email) {
        formDataToSend.append("email", formData.email.trim());
      }

      // Personal Info - only if changed
      if (formData.sonDaughterOf !== initialData.sonDaughterOf) {
        formDataToSend.append("sonDaughterOf", formData.sonDaughterOf.trim());
      }
      
      if (formData.dob !== initialData.dob) {
        formDataToSend.append("dob", formData.dob);
      }

      // Address Info - only if any address field changed
      const addressChanged =
        formData.streetAddress !== initialData.streetAddress ||
        formData.countryId !== initialData.countryId ||
        formData.stateId !== initialData.stateId ||
        formData.districtId !== initialData.districtId ||
        formData.localAreaId !== initialData.localAreaId;

      if (addressChanged) {
        formDataToSend.append("streetAddress", formData.streetAddress || "");
        formDataToSend.append("countryId", formData.countryId || "");
        formDataToSend.append("stateId", formData.stateId || "");
        formDataToSend.append("districtId", formData.districtId || "");
        formDataToSend.append("localAreaId", formData.localAreaId || "");
      }

      // KYC text fields
      if (formData.pincode !== initialData.pincode) {
        formDataToSend.append("pincode", formData.pincode.trim());
      }
      if (formData.aadhaarNumber !== initialData.aadhaarNumber) {
        formDataToSend.append("aadhaarNumber", formData.aadhaarNumber.trim());
      }
      if (formData.panNumber !== initialData.panNumber) {
        formDataToSend.append("panNumber", formData.panNumber.trim());
      }

      // KYC file uploads
      if (formData.aadhaarFront) formDataToSend.append("aadhaarFront", formData.aadhaarFront);
      if (formData.aadhaarBack)  formDataToSend.append("aadhaarBack",  formData.aadhaarBack);
      if (formData.panFront)     formDataToSend.append("panFront",     formData.panFront);
      if (formData.panBack)      formDataToSend.append("panBack",      formData.panBack);

      // Social Profiles - only if changed
      if (formData.facebookProfile !== initialData.facebookProfile) {
        formDataToSend.append("facebookProfile", formData.facebookProfile.trim());
      }
      if (formData.twitterProfile !== initialData.twitterProfile) {
        formDataToSend.append("twitterProfile", formData.twitterProfile.trim());
      }
      if (formData.instagramProfile !== initialData.instagramProfile) {
        formDataToSend.append("instagramProfile", formData.instagramProfile.trim());
      }

      console.log("Updating profile with changed fields only...");

      const response = await api.post("/api/auth/profile", formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log("Profile updated successfully:", response.data);
      toast.success("Profile updated successfully! 🎉");
      
      if (onSave) onSave();
      if (onClose) onClose();

    } catch (error) {
      console.error("Profile update error:", error);
      console.error("Error response:", error.response?.data);
      
      const errorMessage = 
        error.response?.data?.message || 
        error.response?.data?.error ||
        error.message || 
        "Failed to update profile. Please try again.";
        
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-profile-container">
        <div className="text-center p-5">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="edit-profile-container">
      <div className="edit-profile-header">
        <h2>Edit Profile</h2>
        <button className="close-btn" onClick={onClose}>
          <X size={24} />
        </button>
      </div>

      <div className="edit-profile-content">
        {/* Photo Upload */}
        <div className="photo-upload-section">
          <div className="photo-upload" onClick={() => fileInputRef.current?.click()}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />

            <div className="photo-circle">
              {formData.photoPreview ? (
                <img src={formData.photoPreview} alt="user" className="user-photo" />
              ) : (
                <Camera size={40} />
              )}
              <div className="photo-overlay">
                <Upload size={22} />
                <span>Change Photo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="form-section">
          <h3><User size={20} /> Basic Information</h3>
          
          <FormInput 
            icon={User} 
            placeholder="Full Name*" 
            value={formData.fullName}
            onChange={(e) => updateFormData({ fullName: e.target.value })}
            required
          />

          <FormInput 
            icon={Phone} 
            placeholder="Mobile Number*" 
            value={formData.mobileNumber}
            onChange={(e) => updateFormData({ mobileNumber: e.target.value })}
            type="tel"
            disabled={true}
            required
          />
          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '-8px', marginLeft: '44px' }}>
            Mobile number cannot be changed for security reasons
          </p>

          <FormInput 
            icon={Mail} 
            placeholder="Email (optional)" 
            value={formData.email}
            onChange={(e) => updateFormData({ email: e.target.value })}
            type="email"
         
          />
        </div>

        {/* Personal Information */}
        <div className="form-section">
          <h3><User size={20} /> Personal Information</h3>
          
          <FormInput 
            icon={User} 
            placeholder="Son / Daughter of*" 
            value={formData.sonDaughterOf}
            onChange={(e) => updateFormData({ sonDaughterOf: e.target.value })}
            required
          />

          <FormInput
            type="date"
            placeholder="Date of Birth* (DD/MM/YYYY)"
            value={formData.dob}
            onChange={(e) => updateFormData({ dob: e.target.value })}
            max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0]; })()}
            required
          />
          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '-8px', marginLeft: '44px' }}>
            You must be at least 18 years old (DD/MM/YYYY)
          </p>
        </div>

        {/* Address Information */}
        <div className="form-section">
          <h3><Home size={20} /> Address Information</h3>
          <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>
            Leave unchanged if you don't want to update address
          </p>
          
          <FormInput 
            isTextArea 
            icon={Home} 
            placeholder="Full Address"
            value={formData.streetAddress}
            onChange={(e) => updateFormData({ streetAddress: e.target.value })}
          />

          <FormSelect
            icon={Globe}
            placeholder="Select Country"
            options={countries}
            loading={masterLoading}
            value={formData.countryId}
            onChange={handleCountryChange}
          />

          <FormSelect
            icon={MapPin}
            placeholder="Select State"
            options={states}
            loading={masterLoading}
            value={formData.stateId}
            onChange={handleStateChange}
            disabled={!formData.countryId}
          />

          <FormSelect
            icon={MapPin}
            placeholder="Select District"
            options={districts}
            loading={masterLoading}
            value={formData.districtId}
            onChange={handleDistrictChange}
            disabled={!formData.stateId}
          />

          <FormSelect
            icon={MapPin}
            placeholder="Select Local Area"
            options={localAreas}
            loading={masterLoading}
            value={formData.localAreaId}
            onChange={(e) => updateFormData({ localAreaId: e.target.value })}
            disabled={!formData.districtId}
          />

          <FormInput
            icon={Hash}
            placeholder="Pincode (optional)"
            value={formData.pincode}
            onChange={(e) => updateFormData({ pincode: e.target.value })}
            maxLength={10}
          />
        </div>

        {/* KYC Documents */}
        <div className="form-section">
          <h3><ShieldCheck size={20} /> KYC Documents <span style={{ fontSize: '13px', fontWeight: 400, color: '#9ca3af' }}>(Optional)</span></h3>

          <div className="kyc-edit-grid">
            <FormInput
              icon={Hash}
              placeholder="Aadhaar Number"
              value={formData.aadhaarNumber}
              onChange={(e) => updateFormData({ aadhaarNumber: e.target.value })}
              maxLength={12}
            />
            <FormInput
              icon={Hash}
              placeholder="PAN Number"
              value={formData.panNumber}
              onChange={(e) => updateFormData({ panNumber: e.target.value.toUpperCase() })}
              maxLength={10}
            />
          </div>

          <div className="kyc-edit-grid">
            <KycFileInput
              label="Aadhaar Front"
              preview={formData.aadhaarFrontPreview}
              inputRef={aadhaarFrontRef}
              onChange={handleKycFile('aadhaarFront')}
            />
            <KycFileInput
              label="Aadhaar Back"
              preview={formData.aadhaarBackPreview}
              inputRef={aadhaarBackRef}
              onChange={handleKycFile('aadhaarBack')}
            />
            <KycFileInput
              label="PAN Front"
              preview={formData.panFrontPreview}
              inputRef={panFrontRef}
              onChange={handleKycFile('panFront')}
            />
            <KycFileInput
              label="PAN Back"
              preview={formData.panBackPreview}
              inputRef={panBackRef}
              onChange={handleKycFile('panBack')}
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="form-section">
          <h3><Globe size={20} /> Social Links</h3>
          
          <FormInput 
            icon={Facebook} 
            placeholder="Facebook URL (optional)"
            value={formData.facebookProfile}
            onChange={(e) => updateFormData({ facebookProfile: e.target.value })}
            type="url"
          />

          <FormInput 
            icon={Twitter} 
            placeholder="Twitter / X URL (optional)"
            value={formData.twitterProfile}
            onChange={(e) => updateFormData({ twitterProfile: e.target.value })}
            type="url"
          />

          <FormInput 
            icon={Instagram} 
            placeholder="Instagram URL (optional)"
            value={formData.instagramProfile}
            onChange={(e) => updateFormData({ instagramProfile: e.target.value })}
            type="url"
          />
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button 
            className="btn-cancel" 
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="btn-save" 
            onClick={handleSubmit} 
            disabled={saving || masterLoading}
          >
            <Save size={18} />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
       <style jsx>{`
      /* Edit Profile Button */
.btn-edit-profile {
  margin-top: 40px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-edit-profile:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 800px;
  margin-top: 40px;
  width: 100%;
  max-height: 87vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Edit Profile Container */
.edit-profile-container {
  padding: 0;
}

.edit-profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

.edit-profile-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #1f2937;
}

/* Edit Profile Content */
.edit-profile-content {
  padding: 32px;
}

.photo-upload-section {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}

.photo-upload {
  cursor: pointer;
  position: relative;
}

.photo-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
}

.photo-circle:hover {
  transform: scale(1.05);
}

.photo-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.3s;
}

.photo-circle:hover .photo-overlay {
  opacity: 1;
}

/* Form Section */
.form-section {
  margin-bottom: 32px;
}

.form-section h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.form-group {
  margin-bottom: 16px;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.input-container.large {
  align-items: flex-start;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #9ca3af;
  z-index: 1;
}

.form-input {
  width: 100%;
  padding: 12px 12px 12px 44px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.3s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

.form-input.large {
  min-height: 100px;
  resize: vertical;
  padding-top: 12px;
}

select.form-input {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.btn-cancel,
.btn-save {
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.btn-save:disabled,
.btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* KYC Edit Grid */
.kyc-edit-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.kyc-file-input {
  border: 2px dashed #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f9fafb;
  transition: border-color 0.2s;
}

.kyc-file-input:hover {
  border-color: #667eea;
}

.kyc-file-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.kyc-file-preview {
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.kyc-file-pdf-badge {
  font-size: 12px;
  color: #6366f1;
  font-style: italic;
}

.kyc-file-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;
  transition: opacity 0.2s;
}

.kyc-file-btn:hover {
  opacity: 0.88;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
  }

  .edit-profile-content {
    padding: 20px;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-cancel,
  .btn-save {
    width: 100%;
    justify-content: center;
  }

  .kyc-edit-grid {
    grid-template-columns: 1fr;
  }
}
      `}</style>
    </div>
  );
};

export default EditProfile;